import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { canShowAppleIAP } from '../../shared/utils/platform';
import { getCachedAuthUser } from '../../services/network';
import { cancelSecondAskNotification } from '../../services/notifications';
import { useSubscription } from './useSubscription';
import { useEntitlement } from './useEntitlement';

/**
 * useSecondAsk — mounted once in Layout, right after useLapsedRedirect
 * (docs/v151-spec.md §2.5).
 *
 * A week in, an account that never subscribed is shown the plans ONCE more:
 * /paywall?src=day8&reason=week. The server decides when — `secondAsk:
 * { kind: 'day8', since }` on /subscription/status (Free, no subscription
 * history, nothing lapsed, never asked on any device, 7 days since sign-up).
 * Only on iOS (the only place Premium can be bought), only on Free with no
 * Premium shared by a baby's owner, and never while the user is on the
 * paywall, in onboarding, on the offer page, the welcome or baby-setup
 * screens, or opening a pass or an invitation.
 *
 * The acknowledgement is stored per Cognito sub and per `since`. It is written
 * BEFORE navigating, so closing the app on the paywall does not bring the user
 * straight back to it; the paywall's `second_ask_viewed` then clears
 * `secondAsk` on the server for every device. The redirect also withdraws the
 * day-8 notification (9961) on this device: the user has just been asked.
 *
 * v1.5.1 review (docs/v151-spec.md §6.2):
 * - Seen marker: the paywall opened as the second ask (src=day8 or
 *   src=day8-notification) calls markSecondAskSeen() whatever the status says.
 *   Once `bt_second_ask_seen:<sub>` exists the redirect never fires for that
 *   account on this device — checked before the per-`since` acknowledgement,
 *   so a paywall already seen from a notification tap, or one whose
 *   `second_ask_viewed` is still queued offline, is never shown again.
 * - It waits for useEntitlement's `settled`: the own status, the babies list
 *   and the baby-scoped plan all answered, so a caregiver covered by the
 *   owner's Premium is never redirected on a partial answer (a list still
 *   loading, a baby not yet selected, a failed or offline-paused fetch).
 */
export const SECOND_ASK_ACK_PREFIX = 'bt_second_ask:';
export const SECOND_ASK_SEEN_PREFIX = 'bt_second_ask_seen:';
const QUIET_PATHS = ['/paywall', '/onboarding', '/offer', '/welcome', '/baby-setup', '/pass', '/invite'];

function readAck(sub) {
  try {
    return localStorage.getItem(`${SECOND_ASK_ACK_PREFIX}${sub}`);
  } catch {
    return null;
  }
}

function hasSeenSecondAsk(sub) {
  try {
    return localStorage.getItem(`${SECOND_ASK_SEEN_PREFIX}${sub}`) !== null;
  } catch {
    return false;
  }
}

/**
 * Record that this account has seen the second-ask paywall (now, as an ISO
 * time), so the redirect never shows it again on this device. Called by
 * PaywallPage on mount for src=day8 and src=day8-notification, independent of
 * the status. Returns false when there is no signed-in account or no storage.
 */
export function markSecondAskSeen(sub = getCachedAuthUser()?.sub) {
  if (!sub) return false;
  try {
    localStorage.setItem(`${SECOND_ASK_SEEN_PREFIX}${sub}`, new Date().toISOString());
    return true;
  } catch {
    return false;
  }
}

/**
 * Record that this account has seen the second ask for `since` (the server's
 * value, verbatim), so the redirect never repeats it. Returns false when there
 * is no signed-in account, no `since` or no storage.
 */
export function acknowledgeSecondAsk(since, sub = getCachedAuthUser()?.sub) {
  if (!sub || typeof since !== 'string' || !since) return false;
  try {
    localStorage.setItem(`${SECOND_ASK_ACK_PREFIX}${sub}`, since);
    return true;
  } catch {
    return false;
  }
}

export function useSecondAsk() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { plan, secondAsk, lapsed, isLoading } = useSubscription();
  const { sharedByOwner, isLoading: entitlementLoading, settled } = useEntitlement();

  const kind = secondAsk?.kind;
  const since = typeof secondAsk?.since === 'string' && secondAsk.since ? secondAsk.since : null;
  // The server never sends both; should it ever, the lapse is useLapsedRedirect's.
  const hasLapsed = !!lapsed;

  useEffect(() => {
    if (!canShowAppleIAP() || isLoading || entitlementLoading || !settled) return;
    if (plan !== 'free' || sharedByOwner || hasLapsed || kind !== 'day8' || !since) return;
    if (QUIET_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return;
    const sub = getCachedAuthUser()?.sub;
    // Seen on this device (however the user got there) beats any `since`.
    if (!sub || hasSeenSecondAsk(sub) || readAck(sub) === since) return;
    // No storage → no way to remember it: better never than on every launch.
    if (!acknowledgeSecondAsk(since, sub)) return;
    // Asked in the app: the day-8 notification would only repeat it.
    Promise.resolve()
      .then(() => cancelSecondAskNotification())
      .catch(() => {});
    navigate('/paywall?src=day8&reason=week');
  }, [plan, kind, since, sharedByOwner, hasLapsed, isLoading, entitlementLoading, settled, pathname, navigate]);
}

export default useSecondAsk;
